import React from 'react';
import classNames from 'classnames';
import styles from './DesktopMenuItem.scss';
import { isTouchDevice } from '../../../utils/device/identifyDevice';
import { formatTestId } from '../../../utils/test-id/format-testid';

export type SelectionEvent =
  React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;

const PREMIUM_NAV_NON_SHOP_INDICATOR = 'premium-nav-non-shop';

type DesktopMenuItemProps = {
  label: string;
  url: string;
  active: boolean;
  onSelect: (event: SelectionEvent) => void;
  onClose: () => void;
  tabIndex?: number;
  onNavigate?: (direction: string) => void;
  onTabNavigate?: (event: React.KeyboardEvent<HTMLElement>) => void;
  hasChildData?: boolean;
  onLinkClick?: () => void;
};

export const DesktopMenuItem = ({
  label,
  url,
  active,
  onSelect,
  tabIndex = 0,
  onTabNavigate = () => {},
  hasChildData = false,
  onLinkClick,
}: DesktopMenuItemProps) => {
  const renderAsLink = url && !url.includes(PREMIUM_NAV_NON_SHOP_INDICATOR);

  const Element = renderAsLink ? 'a' : 'button';

  const isTouch = isTouchDevice();

  const handleSelect = (e: SelectionEvent) => {
    if (active || !hasChildData) {
      if (renderAsLink) {
        onLinkClick?.();
      }
      return;
    }
    e.preventDefault();
    onSelect(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      onTabNavigate(e);
      return;
    }

    if (e.key === 'Enter') {
      handleSelect(e);
    }
  };

  return (
    <li
      className={styles.desktopMenuItem}
      data-testid={formatTestId(label, 'nav-')}
      suppressHydrationWarning
    >
      <Element
        className={classNames(styles.desktopMenuItemLink, {
          [styles.active]: active,
          [styles.nonTouch]: !isTouch,
        })}
        {...(renderAsLink ? { href: url } : {})}
        {...(isTouch
          ? { onClick: handleSelect }
          : { onMouseOver: handleSelect })}
        onKeyDown={handleKeyDown}
        tabIndex={tabIndex}
        id={formatTestId(label, 'desktop-menu-item-')}
        suppressHydrationWarning
      >
        {label}
      </Element>
    </li>
  );
};
